import React from "react";
import { useTranslation } from "react-i18next";

/**
 * QENVA · CrossCheck Severity Histogram.
 *
 * Compact horizontal bar chart showing finding distribution across
 * Critical/High/Medium/Low. Designed to live on a navy panel as a
 * `leadingSlot` for the Guardian Portfolio header, or standalone
 * below it.
 *
 * Props
 * ─────
 *   histogram : { Critical, High, Medium, Low, Informational }
 *   total     : optional total (defaults to sum of histogram values)
 *   compact   : boolean — slim variant
 *
 * Renders nothing if total === 0.
 */
const SEVERITY_ORDER = ["Critical", "High", "Medium", "Low", "Informational"];
const SEVERITY_COLOR = {
  Critical:      { bar: "bg-rose-500",  dot: "bg-rose-500",  label: "text-rose-200" },
  High:          { bar: "bg-rose-400",  dot: "bg-rose-400",  label: "text-rose-100" },
  Medium:        { bar: "bg-amber-400", dot: "bg-amber-400", label: "text-amber-100" },
  Low:           { bar: "bg-emerald-400", dot: "bg-emerald-400", label: "text-emerald-100" },
  Informational: { bar: "bg-zinc-400",  dot: "bg-zinc-400",  label: "text-zinc-300" },
};

export default function SeverityHistogram({ histogram = {}, total, compact = false, testId = "crosscheck-severity-histogram" }) {
  const { t } = useTranslation("crosscheck");
  const safeHistogram = SEVERITY_ORDER.reduce((acc, k) => {
    acc[k] = histogram[k] || 0;
    return acc;
  }, {});
  const sum = total ?? Object.values(safeHistogram).reduce((a, b) => a + b, 0);
  if (sum === 0) {
    return (
      <div
        data-testid={`${testId}-empty`}
        className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-[12px] text-white/55"
      >
        {t("detail.severity_histogram_empty")}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={testId}>
      {SEVERITY_ORDER.map((sev) => {
        const v = safeHistogram[sev];
        if (v === 0) return null;
        const pct = Math.max(2, Math.round((v / sum) * 100));
        const c = SEVERITY_COLOR[sev];
        return (
          <div key={sev} className="flex items-center gap-3" data-testid={`${testId}-${sev.toLowerCase()}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${c.dot} shrink-0`} />
            <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${c.label} w-[78px] shrink-0`}>{t(`detail.group.${sev}`)}</span>
            <div className="flex-1 h-[6px] rounded-full bg-white/8 overflow-hidden">
              <div className={`h-full ${c.bar} rounded-full`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[12px] font-bold text-white tabular-nums w-[36px] text-right shrink-0">{v}</span>
          </div>
        );
      })}
    </div>
  );
}
